import logger from './logger';
// Notification tones with different audio patterns using Web Audio API
export const notificationTones = {
  classic: {
    name: 'Classic Bell',
    description: 'Traditional bell sound',
    pattern: [
      { frequency: 800, duration: 200, type: 'sine' },
      { frequency: 600, duration: 200, type: 'sine' },
      { frequency: 400, duration: 300, type: 'sine' }
    ]
  },
  chime: {
    name: 'Gentle Chime',
    description: 'Soft, pleasant chime',
    pattern: [
      { frequency: 523, duration: 150, type: 'triangle' },
      { frequency: 659, duration: 150, type: 'triangle' },
      { frequency: 784, duration: 200, type: 'triangle' }
    ]
  },
  notification: {
    name: 'Notification',
    description: 'Standard notification sound',
    pattern: [
      { frequency: 1000, duration: 100, type: 'square' },
      { frequency: 800, duration: 100, type: 'square' },
      { frequency: 1200, duration: 100, type: 'square' }
    ]
  },
  alert: {
    name: 'Alert',
    description: 'Attention-grabbing alert',
    pattern: [
      { frequency: 1200, duration: 100, type: 'sawtooth' },
      { frequency: 800, duration: 100, type: 'sawtooth' },
      { frequency: 1200, duration: 100, type: 'sawtooth' },
      { frequency: 800, duration: 100, type: 'sawtooth' }
    ]
  },
  success: {
    name: 'Success',
    description: 'Positive completion sound',
    pattern: [
      { frequency: 523, duration: 150, type: 'sine' },
      { frequency: 659, duration: 150, type: 'sine' },
      { frequency: 784, duration: 150, type: 'sine' },
      { frequency: 1047, duration: 200, type: 'sine' }
    ]
  },
  coffee: {
    name: 'Coffee Shop',
    description: 'Warm, inviting tone',
    pattern: [
      { frequency: 440, duration: 200, type: 'triangle' },
      { frequency: 554, duration: 200, type: 'triangle' },
      { frequency: 659, duration: 300, type: 'triangle' }
    ]
  },
  digital: {
    name: 'Digital',
    description: 'Modern digital beep',
    pattern: [
      { frequency: 1500, duration: 80, type: 'square' },
      { frequency: 1000, duration: 80, type: 'square' },
      { frequency: 1500, duration: 80, type: 'square' },
      { frequency: 1000, duration: 80, type: 'square' },
      { frequency: 1500, duration: 80, type: 'square' }
    ]
  },
  melodic: {
    name: 'Melodic',
    description: 'Musical notification',
    pattern: [
      { frequency: 523, duration: 200, type: 'sine' },
      { frequency: 659, duration: 200, type: 'sine' },
      { frequency: 784, duration: 200, type: 'sine' },
      { frequency: 659, duration: 200, type: 'sine' },
      { frequency: 784, duration: 300, type: 'sine' }
    ]
  }
};

// Get tone by key
export const getTone = (key) => {
  return notificationTones[key] || notificationTones.classic;
};

// Get all tone names
export const getToneNames = () => {
  return Object.keys(notificationTones);
};

// Get tone options for select dropdown
export const getToneOptions = () => {
  return Object.entries(notificationTones).map(([key, tone]) => ({
    value: key,
    label: tone.name,
    description: tone.description
  }));
};

// Global audio context to reuse and manage autoplay policy
let globalAudioContext = null;
let audioContextInitialized = false;

// Initialize audio context on first user interaction
const initializeAudioContext = async () => {
  if (!globalAudioContext) {
    globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  if (globalAudioContext.state === 'suspended') {
    try {
      await globalAudioContext.resume();
      audioContextInitialized = true;
  logger.debug('AudioContext resumed successfully');
    } catch (error) {
  logger.warn('Failed to resume AudioContext:', error);
      return false;
    }
  } else {
    audioContextInitialized = true;
  }
  
  return true;
};

// Play tone using Web Audio API with complex patterns
export const playTone = async (toneKey, volume = 0.3) => {
  logger.debug('playTone called with:', { toneKey, volume });
  try {
    const tone = getTone(toneKey);
  logger.debug('Selected tone:', tone);
    
    // Initialize audio context if needed
    const initialized = await initializeAudioContext();
    if (!initialized || !globalAudioContext) {
  logger.warn('AudioContext not available');
      return false;
    }
    
    let currentTime = globalAudioContext.currentTime;
    
    tone.pattern.forEach((note, index) => {
      const oscillator = globalAudioContext.createOscillator();
      const gainNode = globalAudioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(globalAudioContext.destination);
      
      oscillator.frequency.setValueAtTime(note.frequency, currentTime);
      oscillator.type = note.type;
      
      // Create a more dynamic volume envelope
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + note.duration / 1000);
      
      oscillator.start(currentTime);
      oscillator.stop(currentTime + note.duration / 1000);
      
      currentTime += note.duration / 1000;
    });
    
    return true;
  } catch (error) {
  logger.warn('Failed to play tone:', error);
    return false;
  }
};

// Initialize audio context on any user interaction
if (typeof window !== 'undefined') {
  const initOnUserGesture = async () => {
    if (!audioContextInitialized) {
      await initializeAudioContext();
      // Remove listeners after first successful initialization
      if (audioContextInitialized) {
        document.removeEventListener('click', initOnUserGesture);
        document.removeEventListener('keydown', initOnUserGesture);
        document.removeEventListener('touchstart', initOnUserGesture);
      }
    }
  };
  
  // Listen for user gestures to initialize audio
  document.addEventListener('click', initOnUserGesture);
  document.addEventListener('keydown', initOnUserGesture);
  document.addEventListener('touchstart', initOnUserGesture);
}
